import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldAlert,
  Bug,
  HandMetal,
  Wind,
  Users2,
  Sparkles,
  Activity,
  ThumbsUp,
  BadgeCheck,
  TrendingUp,
  SprayCan,
  Droplets,
  Waves,
  FlaskConical,
  Trash2,
  Building2,
  CheckCircle2,
  ArrowRight,
  Phone,
  ChevronDown,
  MapPin,
  AlertTriangle,
  Gavel,
  DollarSign,
  Frown,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";

const SITE_URL = "https://deepcleanrr.com";
const CANONICAL = `${SITE_URL}/services/surf-city`;

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/0sf5x9d8_Toliet%20Cleaning%20Sarasota.png";
const INFOGRAPHIC_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/zt41tbqh_Dirty%20Toliet%20Cleaning.png";

const HEALTH_RISKS = [
  {
    icon: Bug,
    title: "Stomach Illnesses",
    desc: "From bacteria like E. coli, Salmonella, and norovirus spread via contaminated toilet seats, handles, and dispensers.",
  },
  {
    icon: HandMetal,
    title: "Skin Irritation & Infections",
    desc: "Caused by contact with contaminated surfaces in stalls, sinks, and high-touch areas.",
  },
  {
    icon: Wind,
    title: "Respiratory Issues",
    desc: "Triggered by mold, mildew, airborne bacteria, and poor restroom ventilation common in North Carolina humidity.",
  },
  {
    icon: Users2,
    title: "Spread of Germs",
    desc: "In Surf City and Topsail Beach workplaces, schools, restaurants, and public facilities, restrooms are the #1 transmission hot-spot.",
  },
];

const SANITATION_BENEFITS = [
  {
    icon: Sparkles,
    title: "Eliminates Harmful Bacteria & Odors",
    desc: "Hospital-grade disinfectants neutralize germs at the source — not just the surface.",
  },
  {
    icon: Wind,
    title: "Improves Indoor Air Quality",
    desc: "Removes mold, mildew, and airborne contaminants for a fresher restroom environment.",
  },
  {
    icon: Activity,
    title: "Reduces Employee Sick Days",
    desc: "Cleaner restrooms mean fewer pathogens — and a healthier, more productive Surf City workforce.",
  },
  {
    icon: ThumbsUp,
    title: "Increases Customer Confidence",
    desc: "Customers judge your entire business by your restroom. A clean one builds trust on every visit.",
  },
  {
    icon: BadgeCheck,
    title: "Avoids Health Code Violations",
    desc: "Stay ahead of inspections, citations, and the legal exposure that comes with neglected sanitation.",
  },
  {
    icon: TrendingUp,
    title: "Protects Reputation & Bottom Line",
    desc: "Clean restrooms drive better reviews, repeat customers, and stronger brand standards.",
  },
];

const PRACTICES = [
  {
    icon: SprayCan,
    title: "Disinfect High-Touch Surfaces",
    desc: "Regularly clean toilet seats, stall doors, faucets, paper towel dispensers, and flush handles.",
  },
  {
    icon: Sparkles,
    title: "Deep Clean Every Surface",
    desc: "Thoroughly clean toilets, urinals, sinks, floors, grout, stall walls, and ceilings.",
  },
  {
    icon: Droplets,
    title: "Odor & Urine Control",
    desc: "Use professional-grade treatments to eliminate odors at the source and prevent buildup.",
  },
  {
    icon: Wind,
    title: "Proper Ventilation",
    desc: "Improve air circulation to reduce moisture, mold, and airborne contaminants.",
  },
  {
    icon: FlaskConical,
    title: "Restock Hygiene Essentials",
    desc: "Keep soap, paper towels, and toilet tissue available to support proper hand hygiene.",
  },
  {
    icon: Trash2,
    title: "Waste & Drain Management",
    desc: "Remove waste regularly and maintain drains to prevent clogs and unpleasant odors.",
  },
];

const BUSINESS_IMPACT = [
  {
    icon: Frown,
    title: "Loss of Customer Trust",
    desc: "Unclean restrooms lead to poor reviews and lost customers — especially in restaurants, retail, and hospitality.",
  },
  {
    icon: AlertTriangle,
    title: "Lower Employee Morale",
    desc: "Employees may avoid using restrooms, causing discomfort, distraction, and lost productivity.",
  },
  {
    icon: DollarSign,
    title: "Increased Maintenance Costs",
    desc: "Neglect leads to long-term damage to plumbing, floors, grout, and fixtures.",
  },
  {
    icon: Gavel,
    title: "Legal & Financial Risks",
    desc: "Health code violations and potential lawsuits can be costly and damaging to your brand.",
  },
];

const DEEP_CLEAN_SERVICES = [
  "Deep Foam Cleaning",
  "High-Pressure Surface Cleaning",
  "Odor & Urine Treatment",
  "Hospital-Grade Disinfecting",
  "Tile & Grout Cleaning",
  "Drain Treatment Services",
  "Complete Restroom Sanitation Programs",
  "Routine Maintenance & Quality Inspections",
  "Eco-Friendly Cleaning Options",
];

const INDUSTRIES = [
  "Restaurants & Cafés",
  "Office Buildings",
  "Retail Stores",
  "Schools & Universities",
  "Medical & Healthcare Facilities",
  "Gyms & Fitness Centers",
  "Hotels & Hospitality",
  "Warehouses & Industrial Facilities",
];

const SERVICE_AREAS = [
  "Surf City",
  "Topsail Beach",
  "Hampstead",
  "Sneads Ferry",
  "Kure Beach",
  "Ogden",
  "Masonboro",
  "Leland",
  "Castle Hayne",
  "Rocky Point",
  "Burgaw",
  "Wallace",
];

const FAQS = [
  {
    q: "What are the biggest health risks of dirty toilets in Surf City businesses?",
    a: "Unclean toilets harbor E. coli, Salmonella, norovirus, and mold — leading to stomach illnesses, skin infections, respiratory issues, and rapid germ spread across staff and customers. North Carolina's humidity makes these problems worse, faster.",
  },
  {
    q: "How often should commercial restrooms in Topsail Beach and Surf City be deep cleaned?",
    a: "Most Surf City and Topsail Beach facilities need daily janitorial maintenance plus a professional deep clean weekly or monthly — depending on foot traffic, industry, and odor complaints.",
  },
  {
    q: "Can professional cleaning actually eliminate restroom odors, or just mask them?",
    a: "We eliminate odors at the source. Deep foam treatment and hospital-grade disinfectants break down urine salts, bacteria, and grime trapped in grout, tile, and drains so odors don't come right back.",
  },
  {
    q: "Do you service Hampstead, Ogden, and Leland?",
    a: "Yes. We serve commercial properties across New Hanover and Pender counties — including Surf City, Topsail Beach, Hampstead, Sneads Ferry, Ogden, Masonboro, Leland, and surrounding communities.",
  },
  {
    q: "Will dirty restrooms really impact my business reputation?",
    a: "Yes. Most customers associate restroom cleanliness with overall business quality, food safety, and professionalism. A neglected restroom drives bad reviews, lost customers, and even health code violations.",
  },
];

const Faq = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`brs-faq-${idx}`} className="border-b border-slate-200 py-5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
        data-testid={`brs-faq-toggle-${idx}`}
      >
        <span className="font-heading text-lg font-bold text-slate-900 md:text-xl">
          {q}
        </span>
        <ChevronDown
          size={20}
          strokeWidth={2}
          className={`shrink-0 text-blue-600 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="mt-3 font-body text-base font-medium leading-relaxed text-slate-600">
          {a}
        </p>
      )}
    </div>
  );
};

const SurfCityServicePage = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Commercial Restroom Sanitation",
    provider: {
      "@type": "LocalBusiness",
      name: "Deep Clean Restroom Services",
      telephone: "+19102369971",
      email: "info@deepcleanrr.com",
      url: SITE_URL,
    },
    areaServed: SERVICE_AREAS.map((a) => ({ "@type": "City", name: a })),
    url: CANONICAL,
    description:
      "Professional commercial restroom cleaning and sanitation across Surf City, Topsail Beach, Hampstead, and surrounding Pender & Topsail Beach County communities. Eliminate bacteria, odors, and the health risks of dirty toilets with hospital-grade disinfection, deep foam cleaning, and complete sanitation programs.",
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <main data-testid="brs-service-page" className="bg-white">
      <SEO
        title="The Impact of Dirty Toilets in Surf City | Restroom Sanitation"
        description="Dirty toilets are a health risk. Discover the bacteria, odors, and business impact of unclean restrooms in Surf City & Topsail Beach — and how Deep Clean's professional sanitation services restore safer, healthier facilities."
        canonical={CANONICAL}
        image={HERO_IMG}
        type="website"
        jsonLd={[jsonLd, faqJsonLd]}
        keywords={[
          "impact of dirty toilets Surf City Topsail Beach",
          "commercial restroom cleaning Topsail Beach",
          "commercial restroom cleaning Surf City",
          "restroom sanitation Pender County",
          "restroom sanitation Topsail Beach County",
          "dirty bathroom health risks North Carolina",
          "deep foam restroom cleaning Topsail Beach",
          "Hampstead janitorial services",
          "Surf City commercial cleaning",
          "Topsail Beach commercial restroom sanitation",
          "urine odor removal Topsail Beach",
          "tile and grout cleaning Surf City",
          "hospital grade disinfection Topsail Beach",
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-slate-950 pt-24">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          aria-hidden="true"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div
          className="absolute inset-0 -z-10"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,9,19,0.86) 0%, rgba(5,9,19,0.60) 30%, rgba(5,9,19,0.45) 100%), linear-gradient(90deg, rgba(5,9,19,0.82) 0%, rgba(5,9,19,0.42) 60%, rgba(5,9,19,0.10) 100%), radial-gradient(60% 50% at 85% 20%, rgba(6,182,212,0.22), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-16 md:px-12 md:pb-32 md:pt-24">
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
            <MapPin size={14} strokeWidth={2} className="text-cyan-300" />
            Surf City · Topsail Beach · New Hanover &amp; Pender Counties
          </p>
          <h1
            data-testid="brs-hero-heading"
            className="max-w-5xl font-heading text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl xl:text-8xl"
          >
            The impact of
            <br />
            <span className="text-cyan-300">dirty toilets</span>
            <br />
            in Surf City.
          </h1>
          <p className="mt-8 max-w-3xl font-body text-base font-medium leading-relaxed text-slate-200 md:text-lg">
            Dirty restrooms are more than just unpleasant — they create serious
            health risks, affect your reputation, and impact the well-being of
            everyone who uses them. Deep Clean restores sanitation standards
            across Surf City, Topsail Beach, Hampstead, and surrounding
            communities.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="brs-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
            >
              Request a Free Quote
              <ArrowRight
                size={18}
                strokeWidth={2.2}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="brs-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              Call 910-236-9971
            </a>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="border-b border-slate-200 bg-white py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:grid-cols-12 md:gap-16 md:px-12">
          <div className="md:col-span-5">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Why Restroom Sanitation Matters
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              A health hazard,
              <br />
              <span className="text-slate-400">hiding in plain sight.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
            <p>
              Unclean toilets and restroom surfaces can quickly become breeding
              grounds for harmful bacteria and viruses such as{" "}
              <strong className="text-slate-900">E. coli</strong>,{" "}
              <strong className="text-slate-900">Salmonella</strong>, and{" "}
              <strong className="text-slate-900">norovirus</strong>. Every
              flush can release airborne germs that spread onto nearby surfaces
              — toilet seats, stall doors, faucets, and paper towel dispensers.
            </p>
            <p>
              For Surf City and Topsail Beach businesses operating in North Carolina's heat
              and humidity, the problem compounds fast. Moisture allows
              bacteria and mildew to grow faster, and high-traffic restrooms
              quickly become silent contributors to employee sick days, poor
              customer reviews, and health code citations.
            </p>
            <p>
              At Deep Clean, we specialize in professional restroom sanitation
              designed to tackle hidden grime, bacteria buildup, and odor at
              the source — not just the surface.
            </p>
          </div>
        </div>
      </section>

      {/* INFOGRAPHIC */}
      <section className="bg-slate-50 py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-6 md:px-12">
          <div className="mb-10 text-center">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — The Full Breakdown
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              The impact of dirty toilets on your health.
            </h2>
            <p className="mt-4 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Save or share this infographic — health risks, sanitation
              practices, business impact, and how Deep Clean restores
              commercial restrooms across Surf City and Topsail Beach.
            </p>
          </div>
          <figure className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
            <img
              src={INFOGRAPHIC_IMG}
              alt="The Impact of Dirty Toilets on Your Health — Deep Clean infographic covering health risks (E. coli, Salmonella, norovirus, mold), why proper sanitation matters, essential restroom cleaning practices, the business impact of dirty restrooms, and how Deep Clean's commercial sanitation services help Surf City and Topsail Beach businesses"
              data-testid="brs-infographic"
              className="w-full"
              loading="lazy"
            />
            <figcaption className="bg-slate-950 px-5 py-3 text-center font-body text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">
              Healthier Spaces · Better Places · Deep Clean
            </figcaption>
          </figure>
        </div>
      </section>

      {/* HEALTH RISKS */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                — Health Risks of Dirty Restrooms
              </p>
              <h2
                data-testid="brs-risks-heading"
                className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
              >
                Unclean restrooms
                <br />
                <span className="text-blue-600">spread real illness.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                Unclean toilets and surfaces can harbor harmful bacteria and
                viruses that spread easily in high-traffic Surf City and
                Topsail Beach facilities.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            {HEALTH_RISKS.map((r, i) => (
              <div
                key={r.title}
                data-testid={`brs-risk-${i}`}
                className="group bg-white p-8 transition-colors hover:bg-slate-950"
              >
                <span className="grid h-12 w-12 place-items-center rounded-md bg-red-500/10 text-red-600 transition-colors group-hover:bg-cyan-300/15 group-hover:text-cyan-300">
                  <r.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-slate-900 transition-colors group-hover:text-white lg:text-xl">
                  {r.title}
                </h3>
                <p className="mt-3 font-body text-sm font-medium leading-relaxed text-slate-600 transition-colors group-hover:text-white/80">
                  {r.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PROPER SANITATION MATTERS */}
      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 max-w-3xl">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Why Proper Sanitation Matters
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              A clean restroom protects health,
              <br />
              <span className="text-slate-400">builds trust, and supports your business.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
            {SANITATION_BENEFITS.map((b, i) => (
              <div
                key={b.title}
                data-testid={`brs-benefit-${i}`}
                className="bg-white p-8"
              >
                <span className="grid h-12 w-12 place-items-center rounded-md bg-blue-600/10 text-blue-600">
                  <b.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-slate-900 lg:text-xl">
                  {b.title}
                </h3>
                <p className="mt-3 font-body text-sm font-medium leading-relaxed text-slate-600">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ESSENTIAL CLEANING PRACTICES */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                — Essential Restroom Cleaning Practices
              </p>
              <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                What real sanitation
                <br />
                <span className="text-blue-600">actually looks like.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                Six core practices every Surf City or Topsail Beach facility should
                follow to keep restrooms safe, healthy, and code-compliant.
              </p>
            </div>
          </div>

          <ol
            data-testid="brs-practices-list"
            className="overflow-hidden rounded-md border border-slate-200 bg-white"
          >
            {PRACTICES.map((s, i) => (
              <li
                key={s.title}
                data-testid={`brs-practice-${i}`}
                className="group grid grid-cols-[auto,auto,1fr,auto] items-start gap-5 border-b border-slate-200 p-6 transition-colors last:border-b-0 hover:bg-slate-50 md:p-8"
              >
                <span className="mt-1 font-heading text-sm font-bold tabular-nums text-slate-400">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="grid h-11 w-11 place-items-center rounded-md border border-slate-200 bg-white text-blue-600 transition-colors group-hover:border-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                  <s.icon size={20} strokeWidth={1.6} />
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold tracking-tight text-slate-900 md:text-xl">
                    {s.title}
                  </h3>
                  <p className="mt-1.5 font-body text-sm font-medium leading-relaxed text-slate-600">
                    {s.desc}
                  </p>
                </div>
                <span
                  className="mt-1 inline-flex h-6 items-center gap-1 rounded-full bg-blue-50 px-2.5 font-body text-[10px] font-bold uppercase tracking-wider text-blue-700"
                  data-testid={`brs-practice-included-${i}`}
                >
                  <CheckCircle2 size={12} strokeWidth={3} />
                  Included
                </span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* BUSINESS IMPACT */}
      <section className="border-y border-slate-200 bg-slate-950 py-20 text-white md:py-28">
        <div
          className="pointer-events-none absolute inset-x-0 -mt-20 h-[200px] opacity-30"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(60% 60% at 80% 20%, rgba(6,182,212,0.30), transparent), radial-gradient(40% 40% at 0% 100%, rgba(239,68,68,0.30), transparent)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
                — The Business Impact
              </p>
              <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                What dirty restrooms
                <br />
                <span className="text-slate-400">actually cost you.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-body text-base font-medium leading-relaxed text-slate-300 md:text-lg">
                A neglected restroom hits your bottom line in four very
                measurable ways.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-md bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {BUSINESS_IMPACT.map((b, i) => (
              <div
                key={b.title}
                data-testid={`brs-impact-${i}`}
                className="bg-slate-950 p-8"
              >
                <span className="grid h-12 w-12 place-items-center rounded-md bg-red-500/15 text-red-400">
                  <b.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-white lg:text-xl">
                  {b.title}
                </h3>
                <p className="mt-3 font-body text-sm font-medium leading-relaxed text-slate-300">
                  {b.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW DEEP CLEAN HELPS */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — How Deep Clean Helps
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Sanitation that goes
              <br />
              <span className="text-blue-600">beyond the surface.</span>
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Deep Clean provides professional restroom sanitation solutions
              that go beyond surface cleaning — built for Surf City, Topsail Beach,
              and New Hanover/Pender County businesses that take hygiene
              seriously.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/#book"
                data-testid="brs-services-book-btn"
                className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
              >
                Get a Free Quote
                <ArrowRight
                  size={18}
                  strokeWidth={2.2}
                  className="transition-transform group-hover:translate-x-1"
                />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ul
              data-testid="brs-services-list"
              className="grid grid-cols-1 gap-3 sm:grid-cols-2"
            >
              {DEEP_CLEAN_SERVICES.map((s, i) => (
                <li
                  key={s}
                  data-testid={`brs-service-${i}`}
                  className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-4 transition-colors hover:border-blue-600"
                >
                  <CheckCircle2
                    size={20}
                    strokeWidth={2}
                    className="mt-0.5 shrink-0 text-blue-600"
                  />
                  <span className="font-body text-sm font-semibold text-slate-800 md:text-base">
                    {s}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 max-w-3xl">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Industries We Serve
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Built for high-traffic Surf City &amp; Topsail Beach facilities.
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {INDUSTRIES.map((ind, i) => (
              <div
                key={ind}
                data-testid={`brs-industry-${i}`}
                className="group flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3.5 transition-colors hover:border-blue-600"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-blue-600/10 text-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                  <Building2 size={14} strokeWidth={2} />
                </span>
                <span className="font-body text-sm font-semibold text-slate-800">
                  {ind}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 max-w-3xl">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Service Areas
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Serving Surf City, Topsail Beach &amp; the Cape Fear Coast.
            </h2>
            <p className="mt-4 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Commercial restroom cleaning and sanitation across Pender and
              Topsail Beach Counties.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {SERVICE_AREAS.map((area, i) => (
              <div
                key={area}
                data-testid={`brs-area-${i}`}
                className="group flex items-center gap-2.5 rounded-md border border-slate-200 bg-white p-3 transition-colors hover:border-blue-600"
              >
                <MapPin
                  size={14}
                  strokeWidth={2}
                  className="shrink-0 text-blue-600"
                />
                <span className="font-body text-[13px] font-semibold text-slate-800">
                  {area}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — FAQ
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Frequently Asked Questions.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Other questions about commercial restroom cleaning in Surf City
              or Topsail Beach? Call{" "}
              <a
                href="tel:9102369971"
                className="font-bold text-blue-600 hover:underline"
              >
                910-236-9971
              </a>
              .
            </p>
          </div>
          <div className="lg:col-span-8">
            {FAQS.map((f, i) => (
              <Faq key={f.q} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-slate-950 py-20 text-white md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.30), transparent), radial-gradient(50% 40% at 0% 100%, rgba(0,85,255,0.40), transparent)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 md:px-12 lg:flex-row lg:items-center">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
              — Surf City · Topsail Beach, NC
            </p>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl lg:text-5xl">
              A cleaner restroom starts with Deep Clean.
            </h2>
            <p className="mt-4 max-w-xl font-body text-sm font-medium text-slate-300 md:text-base">
              Healthier environments. Happier employees &amp; customers.
              Stronger reputation. A better business tomorrow.
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="brs-cta-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
            >
              Get a Free Quote
              <ArrowRight
                size={18}
                strokeWidth={2.2}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="brs-cta-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              910-236-9971
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default SurfCityServicePage;
